import { Link } from 'react-router-dom';
import { ArrowLeft, Pencil, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ContractStatusBadge, GateDecisionBadge } from './ContractStatusBadge';
import type { ContractDetail } from './types';

interface ContractHeaderProps {
  contract: ContractDetail | null;
  isLoading: boolean;
  canEdit: boolean;
  isEditing: boolean;
  onEditToggle: () => void;
}

export function ContractHeader({
  contract,
  isLoading,
  canEdit,
  isEditing,
  onEditToggle,
}: ContractHeaderProps) {
  if (isLoading || !contract) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-8 w-2/3" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-28 rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <nav aria-label="Breadcrumb">
        <Link
          to="/contracts"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Contracts
        </Link>
      </nav>

      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="text-xs font-mono text-muted-foreground tracking-wide">
            {contract.contract_number}
            {contract.version > 1 && (
              <span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded" aria-label={`Version ${contract.version}`}>
                v{contract.version}
              </span>
            )}
          </p>
          <h1 className="text-2xl font-bold text-foreground">{contract.title}</h1>

          <div className="flex flex-wrap items-center gap-2">
            <ContractStatusBadge status={contract.status} />
            <GateDecisionBadge decision={contract.gate_decision} />
            {contract.job_id && (
              <Link
                to={`/jobs/${contract.job_id}`}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring rounded"
                aria-label="View linked pipeline job"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                View Job
              </Link>
            )}
          </div>
        </div>

        {canEdit && (
          <Button
            variant={isEditing ? 'default' : 'outline'}
            size="sm"
            onClick={onEditToggle}
            aria-label={isEditing ? 'Cancel editing contract metadata' : 'Edit contract metadata'}
            aria-pressed={isEditing}
          >
            <Pencil className="h-4 w-4 mr-1.5" aria-hidden="true" />
            {isEditing ? 'Editing' : 'Edit'}
          </Button>
        )}
      </div>
    </div>
  );
}
